import React, { useState, useEffect, useCallback } from 'react';
import { SudokuGame, SudokuCell, formatTime } from '../../utils/gameLogic';
import './SudokuGame.css';

const SudokuGameComponent: React.FC = () => {
  const [game, setGame] = useState(new SudokuGame('easy'));
  const [board, setBoard] = useState<SudokuCell[][]>([]);
  const [isWon, setIsWon] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [selectedNumber, setSelectedNumber] = useState<number>(1);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');

  const updateGameState = useCallback(() => {
    setBoard(game.getBoard());
    setIsWon(game.getIsWon());
    setTimeElapsed(game.getTimeElapsed());
  }, [game]);

  useEffect(() => {
    updateGameState();

    const timer = setInterval(() => {
      setTimeElapsed(game.getTimeElapsed());
    }, 1000);

    return () => clearInterval(timer);
  }, [game, updateGameState]);

  const handleCellClick = (row: number, col: number) => {
    if (game.onCellClick(row, col, selectedNumber)) {
      updateGameState();
    }
  };

  const newGame = (newDifficulty: 'easy' | 'medium' | 'hard') => {
    setDifficulty(newDifficulty);
    setGame(new SudokuGame(newDifficulty));
  };

  const getDifficultyText = () => {
    switch (difficulty) {
      case 'easy': return 'Easy';
      case 'medium': return 'Medium';
      case 'hard': return 'Hard';
      default: return 'Easy';
    }
  };

  return (
    <div className="sudoku-game">
      <div className="game-header">
        <h2>🔢 Sudoku Game</h2>
        <p>Fill in the grid so every row, column, and 3x3 box contains digits 1-9</p>
      </div>
      
      <div className="game-stats">
        <div className="stat-item">
          <span className="stat-label">Difficulty:</span>
          <span className="stat-value">{getDifficultyText()}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Time:</span>
          <span className="stat-value">{formatTime(timeElapsed)}</span>
        </div>
        {isWon && (
          <div className="stat-item win-message">
            <span className="stat-label">Status:</span>
            <span className="stat-value">🎉 Completed!</span>
          </div>
        )}
      </div>

      <div className="difficulty-selector">
        <button 
          className={difficulty === 'easy' ? 'active' : ''}
          onClick={() => newGame('easy')}
        >
          Easy
        </button>
        <button 
          className={difficulty === 'medium' ? 'active' : ''}
          onClick={() => newGame('medium')}
        >
          Medium
        </button>
        <button 
          className={difficulty === 'hard' ? 'active' : ''}
          onClick={() => newGame('hard')}
        >
          Hard
        </button>
      </div>
      
      <div className="sudoku-grid">
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className="sudoku-row">
            {row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={`sudoku-cell ${cell.isOriginal ? 'original' : ''} ${cell.isError ? 'error' : ''} ${isWon ? 'won' : ''}`}
                onClick={() => handleCellClick(rowIndex, colIndex)}
              >
                {cell.value}
              </div>
            ))}
          </div>
        ))}
      </div>
      
      <div className="number-selector">
        <h4>Select Number:</h4>
        <div className="number-buttons">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
            <button
              key={num}
              className={`number-button ${selectedNumber === num ? 'selected' : ''}`}
              onClick={() => setSelectedNumber(num)}
            >
              {num}
            </button>
          ))}
        </div>
      </div>
      
      <div className="game-controls">
        <button onClick={() => newGame(difficulty)} className="reset-button">
          🔄 New Game
        </button>
        {isWon && (
          <button onClick={() => window.location.reload()} className="share-button">
            🎉 Play Again
          </button>
        )}
      </div>
      
      <div className="game-instructions">
        <h4>How to Play:</h4>
        <ul>
          <li>Fill in the empty cells with numbers 1-9</li>
          <li>Each row, column, and 3x3 box must contain all digits 1-9</li>
          <li>Click a cell, then click a number to place it</li>
          <li>Gray cells are pre-filled and cannot be changed</li>
        </ul>
      </div>
    </div>
  );
};

export default SudokuGameComponent;
